import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';

// Feature keys — must match backend Feature class in plan_config.py
export const FEATURES = {
    AI_CHAT: 'ai_chat',
    CONTRACT_MODULE: 'contract_module',
    ESIGNATURE: 'esignature',
    NEWS_HUB: 'news_hub',
    FULL_TEMPLATE_LIBRARY: 'full_template_library',
};

// All languages supported by the platform (ordered by priority)
const ALL_LANGUAGES = [
    { code: "en", name: "English", flag: "🇬🇧", countryCode: "gb" },
    { code: "fr", name: "Français", flag: "🇫🇷", countryCode: "fr" },
    { code: "ar", name: "العربية", flag: "🇸🇦", countryCode: "sa" },
    { code: "es", name: "Español", flag: "🇪🇸", countryCode: "es" },
    { code: "de", name: "Deutsch", flag: "🇩🇪", countryCode: "de" },
    { code: "zh", name: "中文", flag: "🇨🇳", countryCode: "cn" },
    { code: "ja", name: "日本語", flag: "🇯🇵", countryCode: "jp" },
    { code: "ko", name: "한국어", flag: "🇰🇷", countryCode: "kr" },
    { code: "ru", name: "Русский", flag: "🇷🇺", countryCode: "ru" },
    { code: "pt", name: "Português", flag: "🇵🇹", countryCode: "pt" },
    { code: "it", name: "Italiano", flag: "🇮🇹", countryCode: "it" },
    { code: "nl", name: "Nederlands", flag: "🇳🇱", countryCode: "nl" },
    { code: "pl", name: "Polski", flag: "🇵🇱", countryCode: "pl" },
    { code: "tr", name: "Türkçe", flag: "🇹🇷", countryCode: "tr" },
    { code: "hi", name: "हिन्दी", flag: "🇮🇳", countryCode: "in" },
];

const DEFAULT_ALLOWED_LANGUAGE_CODES = ['en', 'fr', 'ar', 'es', 'de'];

const SubscriptionContext = createContext();

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
    // Read subscription directly from Redux — no additional API calls
    const { data: subscription, loading, error } = useSelector(state => state.subscription);
    const userData = useSelector(state => state.auth.user?.user);

    const isSuperAdmin = useMemo(
        () => userData?.roles?.includes('super_admin'),
        [userData?.roles]
    );

    const allowedLanguageCodes = useMemo(() => {
        const count = subscription?.allowed_languages;
        if (typeof count === 'number' && count > 0) {
            return ALL_LANGUAGES.slice(0, count).map(l => l.code);
        }
        return DEFAULT_ALLOWED_LANGUAGE_CODES;
    }, [subscription?.allowed_languages]);

    const isLanguageAvailable = (langCode) => allowedLanguageCodes.includes(langCode);

    const canAddMoreUsers = (currentUserCount) => {
        const max = subscription?.max_users;
        if (!max) return false;
        return currentUserCount < max;
    };

    // Feature checking — checks features map returned by backend
    const isFeatureEnabled = useCallback((featureKey) => {
        if (isSuperAdmin) return true;
        const features = subscription?.features;
        if (!features) return false;
        return features[featureKey] === true;
    }, [subscription?.features, isSuperAdmin]);

    // Get usage stats for a rate-limited feature
    const getUsage = useCallback((featureKey) => {
        const usageList = subscription?.usage || [];
        return usageList.find(u => u.feature === featureKey) || null;
    }, [subscription?.usage]);

    // Check if user has hit the rate limit for a feature
    const isAtLimit = useCallback((featureKey) => {
        if (isSuperAdmin) return false;
        const usage = (subscription?.usage || []).find(u => u.feature === featureKey);
        if (!usage || usage.limit === 0) return false;
        return usage.used >= usage.limit;
    }, [subscription?.usage, isSuperAdmin]);

    return (
        <SubscriptionContext.Provider
            value={{
                subscription,
                loading,
                error,
                isLanguageAvailable,
                canAddMoreUsers,
                maxUsers: subscription?.max_users || 1,
                allowedLanguages: allowedLanguageCodes,
                planType: subscription?.plan || 'starter',
                subscriptionType: subscription?.billing_cycle || 'monthly',
                // Per-seat billing (Pricing V2)
                quantity: subscription?.quantity || 1,
                billingMode: subscription?.billing_mode || 'flat',
                isPerSeat: subscription?.billing_mode === 'per_seat',
                // Plan feature gating
                isFeatureEnabled,
                getUsage,
                isAtLimit,
                isSuperAdmin,
                features: subscription?.features || {},
            }}
        >
            {children}
        </SubscriptionContext.Provider>
    );
};
